import { FunctionComponent, ReactNode, useMemo } from 'react'

import styled, { css, useTheme } from 'styled-components'

import config from '@config'
import useIsDarkModeInSocialButton from '@hooks/useIsDarkModeInSocialButton'
import { SocialButtonType } from '@widgets/SocialShare/types'
import {
  getSocialButtonVariantsMap,
  SocialButtonColorVariant,
  SocialButtonItemType,
} from '@components/Social/Button/utils'
import SvgIcon from '@components/SvgIcon'
import { SvgIconName } from '@components/SvgIcon/types'

const {
  socialembeds: {
    share: { types: socialEmbedTypes },
  },
} = config

interface ButtonProps {
  type: SocialButtonType | 'share'
  onClick: () => void
  className?: string
  children?: ReactNode
  isPopoverOpen?: boolean
  isActive?: boolean
  ariaLabel?: string
  buttonVariant?: SocialButtonColorVariant
}

type StyledButtonProps = Pick<ButtonProps, 'isActive' | 'type'> & {
  isDarkMode?: boolean
  ariaLabel?: string
  buttonStyle: SocialButtonItemType
}

const Wrapper = styled.button<StyledButtonProps>`
  ${({
    isDarkMode,
    buttonStyle: { background: backgroundStyle, border: borderStyle },
  }) => {
    return css`
      display: flex;
      justify-content: center;
      align-items: center;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      border: none;
      cursor: pointer;
      padding: 0;
      line-height: 0;
      appearance: none;
      -webkit-appearance: none;
      -moz-appearance: none;
      user-select: none;
      ${backgroundStyle &&
      css`
        background-color: ${backgroundStyle[
          isDarkMode ? 'darkModeDefault' : 'default'
        ]};
      `};

      ${borderStyle &&
      css`
        border: 1px solid
          ${borderStyle[isDarkMode ? 'darkModeDefault' : 'default']};
        background-color: transparent;
      `};
    `
  }}
`
const OuterWrapper = styled.div<StyledButtonProps>`
  ${({
    theme: {
      spacing: { spacing16 },
      colors: { textDefault },
      typography: {
        bodycopy: {
          small2: { bundledCSS: bodyCopySmall2CSS },
        },
      },
    },
    isDarkMode,
    isActive,
    type,
    buttonStyle: {
      background: backgroundStyle,
      border: borderStyle,
      icon: iconStyle,
    },
  }) => css`
    display: flex;
    cursor: pointer;
    align-items: center;
    gap: ${spacing16};

    ${bodyCopySmall2CSS};

    color: ${textDefault};

    &:hover {
      button {
        ${backgroundStyle &&
        css`
          background-color: ${backgroundStyle[
            isDarkMode ? 'darkModeHover' : 'hover'
          ]};
        `};

        ${borderStyle &&
        css`
          border: 1px solid
            ${borderStyle[isDarkMode ? 'darkModeHover' : 'hover']};
          background-color: transparent;
        `};
      }
    }

    ${isActive &&
    css`
      &:hover {
        button {
          ${backgroundStyle &&
          css`
            background-color: ${backgroundStyle[
              isDarkMode ? 'darkModeHover' : 'hover'
            ]};
          `};

          ${borderStyle &&
          css`
            border: 1px solid
              ${borderStyle[isDarkMode ? 'darkModeHover' : 'hover']};
            background-color: transparent;
          `};

          svg {
            color: ${iconStyle[isDarkMode ? 'darkModeDefault' : 'default']};
            fill: ${iconStyle[isDarkMode ? 'darkModeDefault' : 'default']};
          }
        }
      }
      button {
        ${backgroundStyle &&
        css`
          background-color: ${backgroundStyle[
            isDarkMode ? 'darkModeDefault' : 'hover'
          ]};

          ${type === 'pluslinkshare' &&
          css`
            background-color: ${backgroundStyle.active};
          `}
        `};

        ${borderStyle &&
        css`
          background-color: transparent;
          ${borderStyle[isDarkMode ? 'darkModeDefault' : 'hover']};

          ${type === 'pluslinkshare' &&
          css`
            border: 1px solid ${borderStyle.active};
            svg {
              color: ${iconStyle.active};
              fill: ${iconStyle.active};
            }
          `}
        `};
      }
    `}
  `}
`

const Button: FunctionComponent<ButtonProps> = ({
  type,
  onClick,
  className,
  children,
  isActive,
  ariaLabel,
  buttonVariant,
}) => {
  const theme = useTheme()
  const isDarkMode = useIsDarkModeInSocialButton()
  const iconName =
    type === 'share'
      ? 'share'
      : (socialEmbedTypes[type]?.iconName satisfies SvgIconName)

  const buttonStyle = useMemo(
    () => getSocialButtonVariantsMap(theme.colors)[buttonVariant || 'grey'],
    [buttonVariant, theme.colors],
  )

  const iconColor =
    isDarkMode || (isActive && type === 'pluslinkshare')
      ? buttonStyle.icon.darkModeDefault
      : buttonStyle.icon.default

  return (
    <OuterWrapper
      type={type}
      isActive={isActive}
      isDarkMode={isDarkMode}
      onClick={onClick}
      buttonStyle={buttonStyle}
    >
      <Wrapper
        type={type}
        isDarkMode={isDarkMode}
        className={className}
        aria-label={ariaLabel || type}
        buttonStyle={buttonStyle}
      >
        <SvgIcon iconName={iconName} size={20} color={iconColor} />
      </Wrapper>
      {children}
    </OuterWrapper>
  )
}

export default Button
